import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SUPPORT_BOOKING_URL } from '../../config/support';
import { submitWorkspaceActivationRequest } from '@services/workspaceActivation';

const INTERVIEW_VOLUME_OPTIONS = ['0-20', '20-100', '100-500', '500+'];
const SURVEY_RESPONSE_VOLUME_OPTIONS = ['0-500', '500-5000', '5000+'];

const PRIMARY_USE_CASES = [
  {
    label: 'AI moderated interviews',
    volumeKey: 'ai_moderated_interviews_volume',
    volumeLabel: 'Estimated interviews',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
  },
  {
    label: 'AI participant (synthetic users) responses',
    volumeKey: 'ai_participant_responses_volume',
    volumeLabel: 'Estimated responses',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
  },
  {
    label: 'Survey response analysis',
    volumeKey: 'survey_response_analysis_volume',
    volumeLabel: 'Estimated responses',
    volumeOptions: SURVEY_RESPONSE_VOLUME_OPTIONS,
    infoText:
      'With an activated workspace, there is no charge for data collection and general analysis. Only AI analysis of open-ended responses cost entitlements.',
  },
  {
    label: 'Qualitative analysis',
    volumeKey: 'qualitative_analysis_volume',
    volumeLabel: 'Estimated studies',
    volumeOptions: INTERVIEW_VOLUME_OPTIONS,
    followupKeys: ['custom_analysis_needed', 'slides_report_needed'],
  },
  {
    label: 'Research operations & compliance',
    followupKeys: ['gdpr_compliant', 'data_residency_required'],
  },
];

const SERVICE_OPTIONS = [
  {
    label: 'End-to-end research support',
    tooltip: "We can help 'run' the project end-to-end for you if desired.",
  },
];

const WorkspaceActivationContent = ({ prefill = {}, isExpired = false, variant = 'full' }) => {
  const isEmbedded = variant === 'embedded';
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    user_name: prefill.user_name || '',
    user_email: prefill.user_email || '',
    company_name: prefill.company_name || '',
    role_title: prefill.role_title || '',
    workspace_id: prefill.workspace_id || '',
    workspace_name: prefill.workspace_name || '',
    workspace_member_count: '',
    primary_use_cases: [],
    ai_moderated_interviews_volume: '',
    ai_participant_responses_volume: '',
    survey_response_analysis_volume: '',
    qualitative_analysis_volume: '',
    custom_analysis_needed: '',
    slides_report_needed: '',
    gdpr_compliant: '',
    data_residency_required: '',
    data_residency_details: '',
    recruitment_support: 'not_sure',
    additional_services: [],
    project_description: '',
    notes: '',
    source: prefill.source || 'app',
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      user_name: prefill.user_name || prev.user_name,
      user_email: prefill.user_email || prev.user_email,
      company_name: prefill.company_name || prev.company_name,
      role_title: prefill.role_title || prev.role_title,
      workspace_id: prefill.workspace_id || prev.workspace_id,
      workspace_name: prefill.workspace_name || prev.workspace_name,
      source: prefill.source || prev.source,
    }));
  }, [prefill]);

  const handleToggle = (field, value) => {
    setFormData((prev) => {
      const currentValues = prev[field];
      const exists = currentValues.includes(value);
      return {
        ...prev,
        [field]: exists
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };
    });
  };

  const handlePrimaryUseCaseToggle = (item) => {
    setFormData((prev) => {
      const currentValues = prev.primary_use_cases;
      const exists = currentValues.includes(item.label);
      const nextValues = exists
        ? currentValues.filter((useCase) => useCase !== item.label)
        : [...currentValues, item.label];
      const nextState = {
        ...prev,
        primary_use_cases: nextValues,
      };
      if (exists && item.volumeKey) {
        nextState[item.volumeKey] = '';
      }
      if (exists && item.followupKeys) {
        item.followupKeys.forEach((key) => {
          nextState[key] = '';
        });
      }
      if (!exists && item.followupKeys) {
        item.followupKeys.forEach((key) => {
          if (!nextState[key]) {
            nextState[key] = 'not_sure';
          }
        });
      }
      if (exists && item.label === 'Research operations & compliance') {
        nextState.data_residency_details = '';
      }
      return nextState;
    });
  };

  const validateForm = () => {
    if (!formData.user_name.trim()) {
      return 'Please add your name.';
    }
    if (!formData.user_email.trim()) {
      return 'Please add your email.';
    }
    if (parseNumber(formData.workspace_member_count) === null) {
      return 'Please add the number of workspace members.';
    }
    return '';
  };

  const parseNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    return parsed < 0 ? null : parsed;
  };

  const isScheduleStep = activeStep === 1;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      setStatus({ state: 'error', message: error });
      return;
    }
    setStatus({ state: 'loading', message: '' });
    try {
      const qualitativeSelected = formData.primary_use_cases.includes('Qualitative analysis');
      const researchOpsSelected = formData.primary_use_cases.includes(
        'Research operations & compliance',
      );
      const payload = {
        ...formData,
        workspace_member_count: parseNumber(formData.workspace_member_count),
        project_description: formData.project_description.trim() || null,
        ai_moderated_interviews_volume: formData.ai_moderated_interviews_volume || null,
        ai_participant_responses_volume: formData.ai_participant_responses_volume || null,
        survey_response_analysis_volume: formData.survey_response_analysis_volume || null,
        qualitative_analysis_volume: formData.qualitative_analysis_volume || null,
        custom_analysis_needed: qualitativeSelected ? formData.custom_analysis_needed : null,
        slides_report_needed: qualitativeSelected ? formData.slides_report_needed : null,
        gdpr_compliant: researchOpsSelected ? formData.gdpr_compliant : null,
        data_residency_required: researchOpsSelected ? formData.data_residency_required : null,
        data_residency_details:
          researchOpsSelected && formData.data_residency_required === 'yes'
            ? formData.data_residency_details.trim() || null
            : null,
      };
      await submitWorkspaceActivationRequest(payload);
      setStatus({
        state: 'success',
        message:
          'Thanks for sharing the details. The next step is to schedule a short call so we can activate your workspace.',
      });
      setActiveStep(1);
    } catch (err) {
      setStatus({
        state: 'error',
        message: err?.detail || 'Something went wrong while submitting. Please try again.',
      });
    }
  };

  return (
    <Box
      sx={{
        background: isEmbedded
          ? 'transparent'
          : 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
        py: isEmbedded ? { xs: 4, md: 6 } : { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={4}>
          <Stack spacing={2.5}>
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Activate your Qualz workspace
            </Typography>

            {isExpired && (
              <Alert severity="warning">
                This workspace is expired. Contact sales to renew or request a new activation.
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(48px, 1fr) auto',
                gap: { xs: 1.5, sm: 2 },
                alignItems: 'center',
                py: 1,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    flexShrink: 0,
                  }}
                >
                  1
                </Box>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  Share details
                </Typography>
              </Stack>

              <Box
                sx={{
                  height: 3,
                  borderRadius: 999,
                  bgcolor: isScheduleStep ? 'primary.main' : 'divider',
                }}
              />

              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    bgcolor: isScheduleStep ? 'primary.main' : 'background.paper',
                    color: isScheduleStep ? 'primary.contrastText' : 'text.secondary',
                    border: isScheduleStep ? 'none' : '2px solid',
                    borderColor: isScheduleStep ? 'primary.main' : 'divider',
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={isScheduleStep ? 'primary.main' : 'text.secondary'}
                >
                  Schedule a call
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3,
              background: '#ffffff',
              boxShadow: '0 24px 60px rgba(31, 64, 104, 0.12)',
            }}
          >
            <Stack spacing={2.5}>
              <Typography variant="h5" fontWeight={700}>
                {isScheduleStep ? 'Schedule your activation call' : 'Tell us about your research'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isScheduleStep
                  ? 'Thanks for the detail. Book a short call and we will use it to activate the workspace with the right setup.'
                  : 'Answer a few quick questions so we can prepare the activation call for your team and use case.'}
              </Typography>
              {status.state === 'error' && <Alert severity="error">{status.message}</Alert>}

              {!isScheduleStep ? (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    '& .MuiFormControlLabel-root': { my: 0.125 },
                  }}
                >
                  <Stack spacing={2.25}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Full name"
                          size="small"
                          fullWidth
                          value={formData.user_name}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, user_name: e.target.value }))
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Work email"
                          type="email"
                          size="small"
                          fullWidth
                          value={formData.user_email}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              user_email: e.target.value,
                            }))
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Company"
                          size="small"
                          fullWidth
                          value={formData.company_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              company_name: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Role / title"
                          size="small"
                          fullWidth
                          value={formData.role_title}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, role_title: e.target.value }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Workspace name (if known)"
                          size="small"
                          fullWidth
                          value={formData.workspace_name}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_name: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Workspace ID (if known)"
                          size="small"
                          fullWidth
                          value={formData.workspace_id}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_id: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="How many members in the workspace?"
                          type="number"
                          size="small"
                          fullWidth
                          value={formData.workspace_member_count}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workspace_member_count: e.target.value,
                            }))
                          }
                          inputProps={{ min: 0 }}
                          required
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Estimated Yearly Volume</span>
                          <Tooltip title="If you are estimating for a single project, just use those numbers.">
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Stack>
                      </FormLabel>
                      <FormGroup>
                        {PRIMARY_USE_CASES.map((item) => {
                          const isSelected = formData.primary_use_cases.includes(item.label);
                          const labelContent = (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <span>{item.label}</span>
                              {item.infoText && (
                                <Tooltip title={item.infoText}>
                                  <InfoOutlinedIcon fontSize="small" color="action" />
                                </Tooltip>
                              )}
                            </Stack>
                          );
                          return (
                            <Box key={item.label} sx={{ mb: item.volumeKey ? 2 : 0 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handlePrimaryUseCaseToggle(item)}
                                  />
                                }
                                label={labelContent}
                              />
                              {isSelected && (
                                <Box sx={{ pl: 4 }}>
                                  {item.volumeKey &&
                                    (item.volumeOptions ? (
                                      <TextField
                                        label={item.volumeLabel}
                                        select
                                        size="small"
                                        fullWidth
                                        value={formData[item.volumeKey]}
                                        onChange={(e) =>
                                          setFormData((prev) => ({
                                            ...prev,
                                            [item.volumeKey]: e.target.value,
                                          }))
                                        }
                                        helperText="Select a range"
                                        sx={{ maxWidth: 320 }}
                                      >
                                        {item.volumeOptions.map((option) => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    ) : (
                                      <TextField
                                        label={item.volumeLabel || 'Estimated volume'}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={formData[item.volumeKey]}
                                        onChange={(e) =>
                                          setFormData((prev) => ({
                                            ...prev,
                                            [item.volumeKey]: e.target.value,
                                          }))
                                        }
                                        placeholder={item.volumeExample}
                                        helperText={
                                          item.volumeExample && item.volumeUnit
                                            ? `e.g., ${item.volumeExample} ${item.volumeUnit}`
                                            : undefined
                                        }
                                        inputProps={{ min: 0 }}
                                        sx={{ maxWidth: 320 }}
                                      />
                                    ))}
                                  {item.followupKeys && (
                                    <Stack spacing={2} sx={{ mt: item.volumeKey ? 2 : 1 }}>
                                      {item.label === 'Qualitative analysis' && (
                                        <>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <span>Do you need custom analysis?</span>
                                                <Tooltip title="Custom analysis is when you want to use your own framework, beyond the 14 frameworks or lenses already supported.">
                                                  <InfoOutlinedIcon
                                                    fontSize="small"
                                                    color="action"
                                                  />
                                                </Tooltip>
                                              </Stack>
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.custom_analysis_needed}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  custom_analysis_needed: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              Do you need slides and report built?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.slides_report_needed}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  slides_report_needed: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                        </>
                                      )}
                                      {item.label === 'Research operations & compliance' && (
                                        <>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              GDPR compliant?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.gdpr_compliant}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  gdpr_compliant: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          <FormControl component="fieldset">
                                            <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                                              Other data residency requirements?
                                            </FormLabel>
                                            <RadioGroup
                                              row
                                              value={formData.data_residency_required}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  data_residency_required: e.target.value,
                                                }))
                                              }
                                            >
                                              <FormControlLabel
                                                value="yes"
                                                control={<Radio />}
                                                label="Yes"
                                              />
                                              <FormControlLabel
                                                value="no"
                                                control={<Radio />}
                                                label="No"
                                              />
                                              <FormControlLabel
                                                value="not_sure"
                                                control={<Radio />}
                                                label="Not sure"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                          {formData.data_residency_required === 'yes' && (
                                            <TextField
                                              label="Please specify data residency requirements"
                                              size="small"
                                              fullWidth
                                              value={formData.data_residency_details}
                                              onChange={(e) =>
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  data_residency_details: e.target.value,
                                                }))
                                              }
                                            />
                                          )}
                                        </>
                                      )}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </FormGroup>
                    </FormControl>

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Do you need recruitment support?</span>
                          <Tooltip title="We have access to 13M+ B2B and 100M B2C participants through our partner recruitment providers. We can also work with your internal and external providers.">
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Stack>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.recruitment_support}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            recruitment_support: e.target.value,
                          }))
                        }
                      >
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                        <FormControlLabel value="not_sure" control={<Radio />} label="Not sure" />
                      </RadioGroup>
                    </FormControl>

                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        Services
                      </FormLabel>
                      <FormGroup>
                        {SERVICE_OPTIONS.map((item) => (
                          <FormControlLabel
                            key={item.label}
                            control={
                              <Checkbox
                                checked={formData.additional_services.includes(item.label)}
                                onChange={() => handleToggle('additional_services', item.label)}
                              />
                            }
                            label={
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <span>{item.label}</span>
                                {item.tooltip && (
                                  <Tooltip title={item.tooltip}>
                                    <InfoOutlinedIcon fontSize="small" color="action" />
                                  </Tooltip>
                                )}
                              </Stack>
                            }
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                    <TextField
                      label="Anything else we should know?"
                      multiline
                      minRows={2}
                      size="small"
                      fullWidth
                      value={formData.notes}
                      onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    />

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ sm: 'center' }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={status.state === 'loading'}
                      >
                        {status.state === 'loading' ? 'Saving...' : 'Next'}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        We will use this context to prepare the activation call.
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              ) : (
                <Stack spacing={3}>
                  <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    sx={{ alignItems: 'center' }}
                  >
                    {status.message}
                  </Alert>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        What happens next
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Schedule a short activation call and we will walk through your use case,
                        confirm the right setup, and get the workspace moving.
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2">• Review your research goals</Typography>
                        <Typography variant="body2">• Confirm the right workspace setup</Typography>
                        <Typography variant="body2">
                          • Activate the workspace with the right plan
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                  <Button
                    variant="contained"
                    color="primary"
                    component="a"
                    href={SUPPORT_BOOKING_URL}
                    target="_blank"
                    rel="noreferrer"
                    size="large"
                    sx={{ alignSelf: 'flex-start', px: 3 }}
                  >
                    Schedule a Call
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default WorkspaceActivationContent;
